import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import { FullState } from '../../store';
import { EventsExplorerState } from '../types';

const now = moment();
const aMonthAgo = now.clone().add(-30, 'days');

const initialState: EventsExplorerState = {
  dateFilter: {
    startTime: aMonthAgo,
    endTime: now
  },
  organisations: [],
  aircraft: ['*'],
  eventSubtypes: [],
  eventSeverities: [],
  loadingStatus: {
    events: {
      cancel: false,
      loading: false,
      total: undefined,
      loaded: 0,
      flights: 0
    }
  },
  mapTheme: 'WorldStreetMap'
};

const eventsExplorerSlice = createSlice({
  name: 'eventsExplorer',
  initialState,
  reducers: {
    setDates: (
      state: EventsExplorerState,
      { payload }: { payload: EventsExplorerState['dateFilter'] }
    ) => {
      state.dateFilter = payload;
      return state;
    },
    resetDates: (state: EventsExplorerState) => {
      state.dateFilter.startTime = aMonthAgo;
      state.dateFilter.endTime = now;
      return state;
    },

    setOrganisations: (
      state: EventsExplorerState,
      { payload }: { payload: EventsExplorerState['organisations'] }
    ) => {
      state.organisations = payload;
      return state;
    },

    setAircraft: (
      state: EventsExplorerState,
      { payload }: { payload: EventsExplorerState['aircraft'] }
    ) => {
      state.aircraft = payload;
      return state;
    },

    setEventSubTypes: (
      state: EventsExplorerState,
      { payload }: { payload: EventsExplorerState['eventSubtypes'] }
    ) => {
      state.eventSubtypes = payload;
      return state;
    },

    setEventSeverities: (
      state: EventsExplorerState,
      { payload }: { payload: EventsExplorerState['eventSeverities'] }
    ) => {
      state.eventSeverities = payload;
      return state;
    },

    setEventsLoadingCancel: (state: EventsExplorerState, { payload }: { payload: boolean }) => {
      state.loadingStatus.events.cancel = payload;
      return state;
    },

    setEventsLoadingStatus: (state: EventsExplorerState, { payload }: { payload: boolean }) => {
      state.loadingStatus.events.loading = payload;
      return state;
    },

    setEventsLoadingError: (
      state: EventsExplorerState,
      { payload }: { payload: Error | undefined }
    ) => {
      state.loadingStatus.events.error = payload;
      return state;
    },

    setEventsTotalCount: (state: EventsExplorerState, { payload }: { payload: number }) => {
      state.loadingStatus.events.total = payload;
      return state;
    },

    setEventsLoadedCount: (
      state: EventsExplorerState,
      { payload: { loaded, flights } }: { payload: { loaded: number; flights: number } }
    ) => {
      state.loadingStatus.events.loaded = loaded;
      state.loadingStatus.events.flights = flights;
      return state;
    },

    setMapTheme: (state: EventsExplorerState, { payload }: { payload: string }) => {
      state.mapTheme = payload;
      return state;
    }
  }
});

// Actions:
export const {
  setDates,
  resetDates,
  setOrganisations,
  setAircraft,
  setEventSubTypes,
  setEventSeverities,
  setEventsLoadingCancel,
  setEventsLoadingStatus,
  setEventsLoadedCount,
  setEventsTotalCount,
  setEventsLoadingError,
  setMapTheme
} = eventsExplorerSlice.actions;

// Selectors:
export const getDates = (state: FullState) => state.eventsExplorer.dateFilter;
export const getOrganisations = (state: FullState) => state.eventsExplorer.organisations;
export const getAircraft = (state: FullState) => state.eventsExplorer.aircraft;
export const getEventSubtypes = (state: FullState) => state.eventsExplorer.eventSubtypes;
export const getEventSeverities = (state: FullState) => state.eventsExplorer.eventSeverities;
export const getEventsLoadingInfo = (state: FullState) => state.eventsExplorer.loadingStatus.events;
export const getMapTheme = (state: FullState) => state.eventsExplorer.mapTheme;

export default eventsExplorerSlice;
